import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async"; 
import {
  Shield,
  Lock,
  Eye,
  Cookie,
  Globe,
  Mail,
  Users,
  CheckSquare,
  Scale,
  FileText
} from "lucide-react";
import Aos from "aos";
import "aos/dist/aos.css";
import { useSEO } from "../Pages/Seo";

const PrivacyPolicy = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
    Aos.init({ duration: 500, once: true });
  }, []);

  useSEO({
    title: "Privacy Policy | Game Zone Hub",
    description: "Read the Privacy Policy of Game Zone Hub. We are committed to protecting your personal information and ensuring a safe gaming environment.",
    keywords: "privacy policy, game zone hub privacy, online games privacy, safe browser games, cookies policy, data protection",
    canonical: "https://www.gamezonehub.in/privacy-policy",
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy - Game Zone Hub",
    "description": "Formal privacy policy and data protection guidelines for Game Zone Hub users.",
    "url": "https://www.gamezonehub.in/privacy-policy",
    "publisher": {
      "@type": "Organization",
      "name": "Game Zone Hub"
    }
  };

  const sections = [
    {
      id: "consent",
      title: "1. Consent to Data Processing",
      icon: <CheckSquare className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            By accessing and utilizing <strong>Game Zone Hub</strong>, you hereby acknowledge and provide your informed consent to the data collection and processing practices described in this Privacy Policy. This policy governs how we manage information collected from users during their engagement with our HTML5 gaming portal.
          </p>
          <p>
            This document constitutes a formal agreement between you and Game Zone Hub. If you do not agree with the terms outlined herein, we respectfully request that you discontinue your use of our website immediately. This policy is applicable exclusively to our online activities and does not extend to information collected through offline channels.
          </p>
        </div>
      ),
    },
    {
      id: "data-collection",
      title: "2. Information Acquisition",
      icon: <Eye className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            We adhere to strict data minimization principles. We only acquire personal information that is essential for the operation of our platform.
          </p>
          <p>
            <strong>User-Provided Information:</strong> Should you contact us for technical support or administrative inquiries, you may voluntarily provide personal identifiers, such as your name and email address. We store this information solely for the purpose of resolving your query.
          </p>
          <p>
            <strong>Automated Log Data:</strong> Like most web-based services, our servers automatically record standard log data. This includes your IP address, browser type, ISP details, timestamps, and navigation behavior on our site. This technical data is processed in an anonymized format to monitor platform health and security.
          </p>
        </div>
      ),
    },
    {
      id: "how-we-use",
      title: "3. Utilization of Collected Data",
      icon: <FileText className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>The information we acquire is processed for the following legitimate business purposes:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-400 marker:text-blue-500">
            <li>To facilitate the seamless operation and maintenance of the Game Zone Hub gaming environment.</li>
            <li>To enhance platform performance and troubleshoot technical inconsistencies.</li>
            <li>To protect our infrastructure against unauthorized access, bot attacks, and fraudulent activity.</li>
            <li>To respond to user inquiries and provide necessary technical assistance.</li>
          </ul>
        </div>
      ),
    },
    {
      id: "adsense",
      title: "4. Advertising and Cookie Policy",
      icon: <Cookie className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Game Zone Hub utilizes cookies—small data files stored on your local device—to personalize your session and improve your experience.
          </p>
          <p>
            <strong>Third-Party Advertising:</strong> We collaborate with advertising partners, including Google AdSense, to display non-intrusive advertisements. These third-party vendors may use cookies (such as the DART cookie) to serve advertisements based on your prior visits to our site and other internet locations. You may opt out of personalized ad tracking by visiting the <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noreferrer" className="text-blue-400 underline">Google Ads Settings</a> page.
          </p>
        </div>
      ),
    },
    {
      id: "rights",
      title: "5. User Rights and Data Protection",
      icon: <Scale className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            We recognize and uphold the data protection rights afforded to users under regulations such as the GDPR (General Data Protection Regulation) and the CCPA (California Consumer Privacy Act). These include:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-400 marker:text-cyan-500">
            <li>Right to access: Request a copy of your personal data.</li>
            <li>Right to rectification: Correct incomplete or inaccurate information.</li>
            <li>Right to erasure: Request deletion of your personal data.</li>
            <li>Right to data portability: Obtain your data in a structured, machine-readable format.</li>
          </ul>
        </div>
      ),
    },
    {
      id: "security",
      title: "6. Data Security and Retention",
      icon: <Lock className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            The security of your personal data is a priority. We implement industry-standard technical and organizational measures, including Secure Sockets Layer (SSL) encryption, to safeguard information during transmission.
          </p>
          <p>
            Access to your personal data is restricted to authorized personnel who require it to perform their official duties. While we strive to implement robust security measures, we acknowledge that no electronic transmission or storage method is entirely impenetrable.
          </p>
        </div>
      ),
    },
    {
      id: "children", 
      title: "7. Protection of Minors",
      icon: <Users className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Game Zone Hub does not intentionally collect personally identifiable information from children under the age of 13. If you believe your child has disclosed personal information on our site, please contact our support team immediately so that we may expunge the record.
          </p>
        </div>
      ),
    },
    {
      id: "contact",
      title: "8. Administrative Contact",
      icon: <Mail className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            We reserve the right to amend this Privacy Policy periodically. We recommend checking this page occasionally for updates. For inquiries regarding data protection or the implementation of these policies, contact us at:
          </p>
          <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10 inline-block w-full sm:w-auto">
            <p className="flex flex-wrap items-center gap-2">
              <strong className="text-white">Email:</strong> 
              <a href="mailto:support@gamezonehub.in" className="text-blue-400 font-semibold hover:text-blue-300 transition-colors">support@gamezonehub.in</a>
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="relative bg-[#0b1120] min-h-screen text-gray-300 sm:pt-[120px] pt-[100px] pb-11">
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center md:mb-20 mb-11">
          <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-extrabold mb-3 text-white" data-aos="fade-down">
            Privacy <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">Policy</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto" data-aos="fade-up">
            Effective Date: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 max-w-7xl mx-auto">
          <div className="lg:w-1/3">
            <div className="sticky top-28 bg-[#111827]/80 backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-lg" data-aos="fade-right">
              <h3 className="text-white font-bold text-xl mb-4 border-b border-white/10 pb-3">Contents</h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="group flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
                  >
                    <span className="text-blue-500">{section.icon}</span>
                    <span className="text-sm font-medium">{section.title.replace(/^\d\.\s/, '')}</span>
                  </a>
                ))}
              </nav>
            </div>
          </div>

          <div className="lg:w-2/3 space-y-8">
            {sections.map((section) => (
              <div
                key={section.id}
                id={section.id}
                data-aos="fade-up"
                className="bg-[#111827] rounded-2xl p-6 sm:p-8 border border-white/5 scroll-mt-32 shadow-xl"
              >
                <div className="flex items-center gap-3 mb-5 border-b border-white/5 pb-4">
                  <div className="p-3 bg-blue-500/10 rounded-xl text-blue-400">{section.icon}</div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">{section.title}</h2>
                </div>
                <div className="text-gray-300 leading-relaxed text-sm sm:text-base">{section.content}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;